import React, { useState, useContext, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../../context/AuthContext';
import { ROUTES, serviceRequestDetail } from '../../../constants/routes';
import axios from 'axios';

function EditRequest() {
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    property: '',
    status: 'open',
    statusId: '',
    requestType: '',
    requestTypeId: '',
    location: '',
    locationId: '',
    requester: '',
    date: '',
    permission: false,
    occupied: false,
    priority: 'Low',
    tech: '',
    problem: '',
    notes: '',
    timeSpent: '',
    dateFinished: '',
    followUp: ''
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [images, setImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [previewImages, setPreviewImages] = useState([]);
  const [deletingImage, setDeletingImage] = useState(null);

  // Data states
  const [properties, setProperties] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [requestTypes, setRequestTypes] = useState([]);
  const [locations, setLocations] = useState([]);
  const [requesters, setRequesters] = useState([]);
  const [techs, setTechs] = useState([]);
  const [users, setUsers] = useState([]);

  // Filtered options based on selected property
  const [filteredStatuses, setFilteredStatuses] = useState([]);
  const [filteredRequestTypes, setFilteredRequestTypes] = useState([]);
  const [filteredLocations, setFilteredLocations] = useState([]);
  const [filteredRequesters, setFilteredRequesters] = useState([]);
  const [filteredTechs, setFilteredTechs] = useState([]);

  const priorityOptions = ['Low', 'Medium', 'High'];

  // Fetch all data on component mount
  useEffect(() => {
    fetchRequestDetails();
    fetchAllData();
    fetchImages();
  }, [id]);

  // Filter options when data is loaded and property is set
  useEffect(() => {
    if (formData.property && statuses.length > 0 && requestTypes.length > 0 && locations.length > 0 && requesters.length > 0 && techs.length > 0) {
      filterOptionsByProperty(formData.property);
    }
  }, [formData.property, statuses, requestTypes, locations, requesters, techs]);

  // Filter options when property changes
  useEffect(() => {
    if (formData.property) {
      filterOptionsByProperty(formData.property);
    } else {
      setFilteredStatuses([]);
      setFilteredRequestTypes([]);
      setFilteredLocations([]);
      setFilteredRequesters([]);
      setFilteredTechs([]);
    }
  }, [formData.property, statuses, requestTypes, locations, requesters, techs]);

  const fetchRequestDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/requests/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const request = response.data;
      const newFormData = {
        property: request.property_id?.toString() || '',
        status: request.status || 'open',
        statusId: request.status_id?.toString() || '',
        requestType: request.request_type || '',
        requestTypeId: request.request_type_id?.toString() || '',
        location: request.location || '',
        locationId: request.location_id?.toString() || '',
        requester: request.requester_id?.toString() || '',
        date: request.date ? new Date(request.date).toISOString().split('T')[0] : '',
        permission: request.permission || false,
        occupied: request.occupied || false,
        priority: request.priority || 'Low',
        tech: request.tech_id?.toString() || '',
        problem: request.problem || '',
        notes: request.notes || '',
        timeSpent: request.time_spent || '',
        dateFinished: request.date_finished ? new Date(request.date_finished).toISOString().split('T')[0] : '',
        followUp: request.follow_up || ''
      };
      
      setFormData(newFormData);
    } catch (err) {
      console.error('Failed to fetch request details:', err);
      setError('Failed to load request details');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllData = async () => {
    try {
      // Fetch properties
      const propertiesResponse = await axios.get('/api/properties', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setProperties(propertiesResponse.data);

      // Fetch statuses
      const statusResponse = await axios.get('/api/statuses', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setStatuses(statusResponse.data);

      // Fetch request types
      const requestTypesResponse = await axios.get('/api/request-types', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setRequestTypes(requestTypesResponse.data);

      // Fetch locations
      const locationsResponse = await axios.get('/api/locations', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setLocations(locationsResponse.data);

      // Fetch requesters
      const requestersResponse = await axios.get('/api/requesters', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setRequesters(requestersResponse.data);

      // Fetch assigned technicians
      const assignedResponse = await axios.get('/api/assigned', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setTechs(assignedResponse.data);

      // Fetch users
      const usersResponse = await axios.get('/api/users', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setUsers(usersResponse.data);

    } catch (err) {
      console.error('Failed to fetch data:', err);
    }
  };

  const fetchImages = async () => {
    try {
      const response = await axios.get(`/api/requests/${id}/images`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setImages(response.data);
    } catch (err) {
      console.error('Failed to fetch images:', err);
    }
  };

  const filterOptionsByProperty = (propertyId) => {
    const propertyIdNum = parseInt(propertyId);
    
    // Filter statuses
    const filteredStatus = statuses.filter(status => status.property_id === propertyIdNum);
    setFilteredStatuses(filteredStatus);

    // Filter request types
    const filteredRequestType = requestTypes.filter(type => type.property_id === propertyIdNum);
    setFilteredRequestTypes(filteredRequestType);

    // Filter locations
    const filteredLocation = locations.filter(location => location.property_id === propertyIdNum);
    setFilteredLocations(filteredLocation);

    // Filter requesters
    const filteredRequester = requesters.filter(requester => requester.property_id === propertyIdNum);
    setFilteredRequesters(filteredRequester);

    // Filter techs by property
    const filteredTech = techs.filter(tech => tech.property_id === propertyIdNum);
    setFilteredTechs(filteredTech);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      };

      // Handle ID fields for dropdowns
      if (name === 'status') {
        const selectedStatus = filteredStatuses.find(status => status.name === value);
        newData.statusId = selectedStatus ? selectedStatus.id : '';
        
        // Handle status change logic
        if (value.toLowerCase() === 'closed') {
          // Set date finished to today if status is changed to closed
          newData.dateFinished = new Date().toISOString().split('T')[0];
        } else if (prev.status.toLowerCase() === 'closed' && value.toLowerCase() !== 'closed') {
          // Clear date finished if status is changed from closed to something else
          newData.dateFinished = '';
        }
      } else if (name === 'requestType') {
        const selectedRequestType = filteredRequestTypes.find(type => type.name === value);
        newData.requestTypeId = selectedRequestType ? selectedRequestType.id : '';
      } else if (name === 'location') {
        const selectedLocation = filteredLocations.find(location => location.name === value);
        newData.locationId = selectedLocation ? selectedLocation.id : '';
      }

      return newData;
    });
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    setNewImages(files);
    
    // Create preview URLs
    const previews = files.map(file => URL.createObjectURL(file));
    setPreviewImages(previews);
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm('Are you sure you want to delete this image?')) {
      return;
    }

    try {
      setDeletingImage(imageId);
      await axios.delete(`/api/requests/${id}/images/${imageId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      // Remove the image from the local state
      setImages(prevImages => prevImages.filter(img => img.id !== imageId));
    } catch (err) {
      console.error('Failed to delete image:', err);
      alert('Failed to delete image. Please try again.');
    } finally {
      setDeletingImage(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const requestData = {
        requestType: formData.requestType,
        requestTypeId: formData.requestTypeId,
        location: formData.location,
        locationId: formData.locationId,
        statusId: formData.statusId,
        requesterId: formData.requester,
        problem: formData.problem,
        date: formData.date,
        permission: formData.permission,
        occupied: formData.occupied,
        priority: formData.priority,
        tech: formData.tech,
        notes: formData.notes,
        timeSpent: formData.timeSpent,
        dateFinished: formData.dateFinished,
        followUp: formData.followUp,
        property: formData.property
      };

      await axios.put(`/api/requests/${id}`, requestData, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      // Upload new images if any
      if (newImages.length > 0) {
        const formDataImages = new FormData();
        newImages.forEach(image => {
          formDataImages.append('images', image);
        });

        await axios.post(`/api/requests/${id}/images`, formDataImages, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      }

      navigate(serviceRequestDetail(id));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update service request');
    } finally {
      setSaving(false);
    }
  };

  const getPropertyName = (propertyId) => {
    const property = properties.find(p => p.id === parseInt(propertyId));
    return property ? property.name : 'Unknown Property';
  };

  const getRequesterName = (requesterId) => {
    const user = users.find(u => u.id === parseInt(requesterId));
    return user ? user.name : 'Unknown User';
  };

  if (loading) {
    return (
      <div className="container mt-4 app-shell-page">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2">Loading request details...</p>
        </div>
      </div>
    );
  }

  if (error && !formData.property) {
    return (
      <div className="container mt-4 app-shell-page">
        <div className="alert alert-danger">
          {error}
        </div>
        <button onClick={() => navigate(ROUTES.SERVICE_REQUESTS)} className="btn btn-primary">
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="container mt-4 app-shell-page">
      <div className="row">
        <div className="col-12">
          <div className="app-page-header d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4">
            <div>
              <h1 className="h3 mb-1">Edit Service Request</h1>
              <p className="text-muted small mb-0">Save changes when finished.</p>
            </div>
            <div className="d-flex flex-wrap gap-2 flex-shrink-0">
              <button
                type="submit"
                form="editRequestForm"
                className="btn btn-primary btn-sm"
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                onClick={() => navigate(serviceRequestDetail(id))}
                className="btn btn-secondary btn-sm"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => navigate(ROUTES.SERVICE_REQUESTS)}
                className="btn btn-outline-secondary btn-sm"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>

      <form id="editRequestForm" onSubmit={handleSubmit}>
        <div className="row">
          <div className="col-md-6">
            <div className="card app-card mb-4">
              <div className="card-body">
                <h5 className="card-title">Request Details</h5>
                
                <div className="mb-3">
                  <label className="form-label">Property *</label>
                  <select 
                    name="property" 
                    value={formData.property} 
                    onChange={handleInputChange}
                    className="form-select"
                    required
                  >
                    <option value="">Select Property</option>
                    {properties.map(property => (
                      <option key={property.id} value={property.id}>
                        {property.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">Status</label>
                  <select 
                    name="status" 
                    value={formData.status} 
                    onChange={handleInputChange}
                    className="form-select"
                    disabled={!formData.property}
                  >
                    <option value="">Select Status</option>
                    {/* Show current status if it's not in filtered list */}
                    {formData.status && !filteredStatuses.find(s => s.name === formData.status) && (
                      <option value={formData.status}>{formData.status}</option>
                    )}
                    {filteredStatuses.map(status => (
                      <option key={status.id} value={status.name}>{status.name}</option>
                    ))}
                  </select>
                  {!formData.property && (
                    <div className="form-text text-muted">Please select a property first</div>
                  )}
                  <div className="form-text">
                    Current: {formData.status} | Available: {filteredStatuses.map(s => s.name).join(', ')}
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Request Type *</label>
                  <select 
                    name="requestType" 
                    value={formData.requestType} 
                    onChange={handleInputChange}
                    className="form-select"
                    required
                    disabled={!formData.property}
                  >
                    <option value="">Select Request Type</option>
                    {filteredRequestTypes.map(type => (
                      <option key={type.id} value={type.name}>{type.name}</option>
                    ))}
                  </select>
                  {!formData.property && (
                    <div className="form-text text-muted">Please select a property first</div>
                  )}
                </div>

                <div className="mb-3">
                  <label className="form-label">Location *</label>
                  <select 
                    name="location" 
                    value={formData.location} 
                    onChange={handleInputChange}
                    className="form-select"
                    required
                    disabled={!formData.property}
                  >
                    <option value="">Select Location</option>
                    {filteredLocations.map(location => (
                      <option key={location.id} value={location.name}>{location.name}</option>
                    ))}
                  </select>
                  {!formData.property && (
                    <div className="form-text text-muted">Please select a property first</div>
                  )}
                </div>

                <div className="mb-3">
                  <label className="form-label">Requester</label>
                  <select 
                    name="requester" 
                    value={formData.requester} 
                    onChange={handleInputChange}
                    className="form-select"
                    disabled={!formData.property}
                  >
                    <option value="">Select Requester</option>
                    {filteredRequesters.map(requester => (
                      <option key={requester.id} value={requester.id}>{requester.name}</option>
                    ))}
                  </select>
                  {!formData.property && (
                    <div className="form-text text-muted">Please select a property first</div>
                  )}
                </div>

                <div className="mb-3">
                  <label className="form-label">Date</label>
                  <input 
                    type="date" 
                    name="date" 
                    value={formData.date} 
                    onChange={handleInputChange}
                    className="form-control"
                    required
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Priority</label>
                  <select 
                    name="priority" 
                    value={formData.priority} 
                    onChange={handleInputChange}
                    className="form-select"
                  >
                    {priorityOptions.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label">Tech</label>
                  <select 
                    name="tech" 
                    value={formData.tech} 
                    onChange={handleInputChange}
                    className="form-select"
                    disabled={!formData.property}
                  >
                    <option value="">Select Tech</option>
                    {filteredTechs.map(tech => (
                      <option key={tech.id} value={tech.id}>{tech.technician}</option>
                    ))}
                  </select>
                  {!formData.property && (
                    <div className="form-text text-muted">Please select a property first</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-6">
            <div className="card app-card mb-4">
              <div className="card-body">
                <h5 className="card-title">Problem Description</h5>
                
                <div className="mb-3">
                  <label className="form-label">Problem *</label>
                  <textarea 
                    name="problem" 
                    value={formData.problem} 
                    onChange={handleInputChange}
                    className="form-control"
                    rows="4"
                    required
                    placeholder="Describe the problem..."
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Notes</label>
                  <textarea 
                    name="notes" 
                    value={formData.notes} 
                    onChange={handleInputChange}
                    className="form-control"
                    rows="3"
                    placeholder="Additional notes..."
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Follow Up</label>
                  <textarea 
                    name="followUp" 
                    value={formData.followUp} 
                    onChange={handleInputChange}
                    className="form-control"
                    rows="3"
                    placeholder="Follow up actions..."
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row">
          <div className="col-md-6">
            <div className="card app-card mb-4">
              <div className="card-body">
                <h5 className="card-title">Additional Information</h5>
                
                <div className="mb-3">
                  <div className="form-check">
                    <input 
                      type="checkbox" 
                      name="permission" 
                      checked={formData.permission} 
                      onChange={handleInputChange}
                      className="form-check-input"
                    />
                    <label className="form-check-label">Permission Granted</label>
                  </div>
                </div>

                <div className="mb-3">
                  <div className="form-check">
                    <input 
                      type="checkbox" 
                      name="occupied" 
                      checked={formData.occupied} 
                      onChange={handleInputChange}
                      className="form-check-input"
                    />
                    <label className="form-check-label">Space Occupied</label>
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label">Time Spent</label>
                  <input 
                    type="time" 
                    name="timeSpent" 
                    value={formData.timeSpent} 
                    onChange={handleInputChange}
                    className="form-control"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Date Finished</label>
                  <input 
                    type="date" 
                    name="dateFinished" 
                    value={formData.dateFinished} 
                    onChange={handleInputChange}
                    className="form-control"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Images Section */}
        <div className="row">
          <div className="col-12">
            <div className="card app-card mb-4">
              <div className="card-body">
                <h5 className="card-title">Photos</h5>
                
                {/* Existing Images */}
                {images.length > 0 && (
                  <div className="mb-4">
                    <h6>Existing Photos</h6>
                    <div className="row">
                      {images.map((image, index) => (
                        <div key={image.id} className="col-md-3 mb-3">
                          <div className="border rounded p-2 bg-light position-relative">
                            <img 
                              src={image.image_url} 
                              alt={`Service Request Image ${index + 1}`} 
                              className="img-fluid rounded"
                              style={{ width: '100%', height: '150px', objectFit: 'cover' }}
                            />
                            <button
                              type="button"
                              className="btn btn-danger btn-sm position-absolute top-0 end-0 m-1"
                              onClick={() => handleDeleteImage(image.id)}
                              disabled={deletingImage === image.id}
                              style={{ zIndex: 10 }}
                            >
                              {deletingImage === image.id ? (
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                              ) : (
                                '×'
                              )}
                            </button>
                            <div className="mt-2 text-muted small">
                              Uploaded: {new Date(image.uploaded_at).toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Upload New Images */}
                <div className="mb-3">
                  <label className="form-label">Upload Additional Images</label>
                  <input 
                    type="file" 
                    multiple 
                    accept="image/*"
                    capture="environment"
                    onChange={handleImageChange}
                    className="form-control"
                  />
                  <div className="form-text">You can select multiple images from your device or take photos with your camera</div>
                </div>

                {/* New Image Previews */}
                {previewImages.length > 0 && (
                  <div className="mb-3">
                    <label className="form-label">New Image Previews</label>
                    <div className="row">
                      {previewImages.map((preview, index) => (
                        <div key={index} className="col-md-3 mb-2">
                          <img 
                            src={preview} 
                            alt={`Preview ${index + 1}`} 
                            className="img-thumbnail"
                            style={{ width: '100%', height: '150px', objectFit: 'cover' }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger mb-3">
            {error}
          </div>
        )}
      </form>
    </div>
  );
}

export default EditRequest; 